import { ArrowRight, CheckCircle2, Star, Phone, Zap, Shield, Clock, ChevronDown, AlertCircle, Calendar, Truck, Package, Users } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Contact from '../components/Contact';

export default function ExpressUmzuege() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const scrollToContact = () => {
    const element = document.getElementById('kontakt');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const benefits = [
    'Umzug innerhalb 24-48 Stunden möglich',
    'Sofortige Verfügbarkeit unseres Teams',
    'Express-Zollabwicklung für schnellen Transport',
    'Priorität bei allen Umzugsschritten',
    'Flexibel bei Tag und Nacht',
    'Vollständig versichert trotz Zeitdruck',
  ];

  const urgentSituations = [
    {
      icon: AlertCircle,
      title: 'Jobwechsel',
      description: 'Sie haben kurzfristig eine neue Stelle in Deutschland angenommen und müssen schnell umziehen.',
      timeframe: 'Oft innerhalb 1-2 Wochen',
    },
    {
      icon: Calendar,
      title: 'Wohnungskündigung',
      description: 'Ihre Wohnung wurde unerwartet gekündigt oder Sie müssen überraschend ausziehen.',
      timeframe: 'Sofort bis 2 Wochen',
    },
    {
      icon: Users,
      title: 'Familiäre Notfälle',
      description: 'Familiäre Umstände erfordern einen schnellen Wohnortwechsel nach Deutschland.',
      timeframe: 'So schnell wie möglich',
    },
    {
      icon: Truck,
      title: 'Geschäftliche Dringlichkeit',
      description: 'Ihr Unternehmen benötigt Sie dringend am neuen Standort in Deutschland.',
      timeframe: 'Innerhalb weniger Tage',
    },
  ];

  const expressPackages = [
    {
      title: 'Express 48h',
      timeframe: 'Innerhalb 2 Tagen',
      price: 'Regulärer Preis + 30%',
      description: 'Für dringende Umzüge mit etwas Vorlauf',
      features: [
        'Umzug innerhalb 48 Stunden',
        'Verpackungsmaterial sofort verfügbar',
        'Express-Zollabwicklung',
        'Bevorzugte Behandlung',
        'Vollständige Versicherung',
      ],
    },
    {
      title: 'Express 24h',
      timeframe: 'Innerhalb 24 Stunden',
      price: 'Regulärer Preis + 50%',
      popular: true,
      description: 'Wenn es wirklich schnell gehen muss',
      features: [
        'Umzug innerhalb 24 Stunden',
        'Sofortiger Start möglich',
        'Express-Zollabwicklung',
        'Priorität bei allem',
        'Vollständige Versicherung',
        'Nacht- und Wochenendarbeit',
      ],
    },
    {
      title: 'Express Notfall',
      timeframe: 'Innerhalb 12 Stunden',
      price: 'Individuelle Berechnung',
      description: 'Für absolute Notfälle',
      features: [
        'Start innerhalb weniger Stunden',
        'Maximale Ressourcen',
        'Sofortige Zollabwicklung',
        'Absolut höchste Priorität',
        'Vollständige Versicherung',
        'Rund-um-die-Uhr Service',
        'Dediziertes Notfall-Team',
      ],
    },
  ];

  const howItWorks = [
    {
      step: '1',
      title: 'Sofort-Kontakt',
      description: 'Rufen Sie uns an oder nutzen Sie das Kontaktformular. Wir melden uns innerhalb von 30 Minuten zurück.',
      duration: '30 Minuten',
    },
    {
      step: '2',
      title: 'Schnell-Offerte',
      description: 'Nach kurzer Bedarfsermittlung (telefonisch oder per Video) erhalten Sie sofort eine Offerte.',
      duration: '1-2 Stunden',
    },
    {
      step: '3',
      title: 'Sofortiger Start',
      description: 'Nach Ihrer Zusage starten wir umgehend. Verpackungsmaterial wird sofort bereitgestellt.',
      duration: 'Sofort',
    },
    {
      step: '4',
      title: 'Express-Transport',
      description: 'Schneller Transport mit Express-Zollabwicklung direkt an Ihre neue Adresse.',
      duration: '1 Tag',
    },
    {
      step: '5',
      title: 'Schneller Aufbau',
      description: 'Auspacken und Aufbau Ihrer Möbel – damit Sie sofort einziehen können.',
      duration: 'Noch am selben Tag',
    },
  ];

  const whyChooseUs = [
    {
      icon: Zap,
      title: 'Blitzschnell verfügbar',
      description: 'Wir haben immer ein Express-Team auf Abruf. Auch an Wochenenden und Feiertagen sind wir für Sie da.',
    },
    {
      icon: Shield,
      title: 'Keine Qualitätseinbussen',
      description: 'Trotz Zeitdruck arbeiten wir sorgfältig und professionell. Ihre Sachen sind vollständig versichert.',
    },
    {
      icon: Truck,
      title: 'Eigene Express-Flotte',
      description: 'Unsere eigenen LKW sind immer bereit. Keine Wartezeiten auf externe Partner.',
    },
    {
      icon: Clock,
      title: '24/7 Erreichbar',
      description: 'Tag und Nacht, Wochenende und Feiertage – wir sind immer für Sie da, wenn es dringend ist.',
    },
  ];

  const expressServices = [
    {
      title: 'Express-Zollabwicklung',
      description: 'Wir haben direkten Kontakt zu den Zollbehörden und können Ihre Dokumente priorisiert bearbeiten lassen. Das spart wertvolle Zeit.',
      icon: CheckCircle2,
    },
    {
      title: 'Sofort-Verpackung',
      description: 'Unser Team bringt alles Nötige mit und verpackt Ihre Sachen vor Ort. Sie müssen nichts vorbereiten.',
      icon: Package,
    },
    {
      title: 'Nacht- & Wochenendumzüge',
      description: 'Wir ziehen auch nachts oder am Wochenende um – ganz nach Ihrer Dringlichkeit und Verfügbarkeit.',
      icon: Clock,
    },
    {
      title: 'Direkttransport',
      description: 'Keine Umwege, keine Zwischenstopps. Ihre Sachen werden direkt von A nach B transportiert.',
      icon: Truck,
    },
  ];

  const preparation = [
    {
      title: 'Was Sie tun können',
      items: [
        'Persönliche Gegenstände zusammenlegen',
        'Wichtige Dokumente griffbereit halten',
        'Wertgegenstände separat verpacken',
        'Zugangscodes bereithalten',
        'Neue Adresse kommunizieren',
      ],
    },
    {
      title: 'Was wir übernehmen',
      items: [
        'Komplette Verpackung vor Ort',
        'Möbel abbauen',
        'Alles transportieren',
        'Zollabwicklung',
        'Möbel wieder aufbauen',
        'Express-Endreinigung (optional)',
      ],
    },
  ];

  const pricing = [
    {
      situation: 'Express 48h',
      surcharge: '+ 30%',
      example: 'CHF 3\'000 → CHF 3\'900',
      description: 'Auf den regulären Umzugspreis',
    },
    {
      situation: 'Express 24h',
      surcharge: '+ 50%',
      example: 'CHF 3\'000 → CHF 4\'500',
      description: 'Auf den regulären Umzugspreis',
    },
    {
      situation: 'Express Notfall',
      surcharge: 'Individuell',
      example: 'Ab CHF 5\'000',
      description: 'Je nach Situation und Aufwand',
    },
  ];

  const testimonials = [
    {
      name: 'Michael S.',
      location: 'Zürich → Stuttgart',
      situation: 'Jobwechsel',
      text: 'Habe montags die Zusage bekommen und sollte Freitag anfangen. Innerhalb 3 Tagen war alles erledigt. Wahnsinn!',
      rating: 5,
    },
    {
      name: 'Anna K.',
      location: 'Basel → München',
      situation: 'Wohnungskündigung',
      text: 'Meine Wohnung wurde überraschend verkauft. Das Express-Team hat alles in 48h geschafft. Riesige Erleichterung!',
      rating: 5,
    },
    {
      name: 'Thomas B.',
      location: 'Bern → Frankfurt',
      situation: 'Familiärer Notfall',
      text: 'Musste dringend zur Familie nach Deutschland. Der Express-Umzug lief reibungslos trotz Zeitdruck. Danke!',
      rating: 5,
    },
  ];

  const faqs = [
    {
      question: 'Wie schnell können Sie einen Express-Umzug durchführen?',
      answer: 'Wir bieten verschiedene <strong>Express-Optionen</strong>:<br><br>• <strong>Express 48h:</strong> Umzug innerhalb 2 Tagen (Aufpreis +30%)<br>• <strong>Express 24h:</strong> Umzug innerhalb 1 Tag (Aufpreis +50%)<br>• <strong>Express Notfall:</strong> Start innerhalb weniger Stunden (individuell)<br><br>Nach Ihrem Anruf können wir meist <strong>innerhalb 30-60 Minuten</strong> eine Offerte erstellen und bei Zusage <strong>sofort starten</strong>. Auch an Wochenenden und Feiertagen.'
    },
    {
      question: 'Was kostet ein Express-Umzug?',
      answer: 'Ein Express-Umzug kostet mehr als ein regulärer Umzug, da wir:<br>• Andere Aufträge umplanen müssen<br>• Zusätzliche Ressourcen bereitstellen<br>• Überstunden und Wochenendarbeit einplanen<br>• Express-Zollabwicklung durchführen<br><br><strong>Aufpreise:</strong><br>• Express 48h: <strong>+30%</strong> auf regulären Preis<br>• Express 24h: <strong>+50%</strong> auf regulären Preis<br>• Express Notfall: <strong>individuell</strong> (meist +70-100%)<br><br>Beispiel: Regulärer Umzug CHF 3\'000 → Express 24h CHF 4\'500'
    },
    {
      question: 'Ist ein Express-Umzug auch an Wochenenden oder Feiertagen möglich?',
      answer: 'Ja, <strong>absolut</strong>! Gerade bei Express-Umzügen ist <strong>Flexibilität entscheidend</strong>.<br><br>Wir ziehen um:<br>• <strong>Samstag & Sonntag:</strong> Kein Problem<br>• <strong>Feiertage:</strong> Wenn nötig, ja<br>• <strong>Nachts:</strong> Auch möglich (z.B. 22:00-06:00 Uhr)<br>• <strong>Sonn- und Feiertage:</strong> Kleiner Zusatzaufpreis<br><br>Besonders beliebt: <strong>Freitag Abend ausziehen</strong>, über das Wochenende transportieren und aufbauen, <strong>Montag einziehen</strong>.'
    },
    {
      question: 'Wie läuft die Zollabwicklung bei einem Express-Umzug ab?',
      answer: 'Bei Express-Umzügen nutzen wir unsere <strong>Express-<Link to="/zollabwicklung" className="text-slate-900 font-semibold hover:underline">Zollabwicklung</Link></strong>:<br><br><strong>Normale Zollabwicklung:</strong> 2-5 Tage<br><strong>Express-Zollabwicklung:</strong> 1 Tag oder weniger<br><br><strong>So funktioniert es:</strong><br>• Wir haben direkten Kontakt zu Zollbehörden<br>• Alle Dokumente werden vorab digital übermittelt<br>• Prioritäre Behandlung bei der Abfertigung<br>• Oft Abfertigung noch am selben Tag<br><br>Unsere Erfahrung bei <Link to="/umzug-zuerich-deutschland" className="text-slate-900 font-semibold hover:underline">Umzügen Zürich Deutschland</Link> und Kontakte machen den Unterschied – wir wissen genau, wie es schnell geht!'
    },
    {
      question: 'Muss ich bei einem Express-Umzug selbst mithelfen?',
      answer: 'Nein, <strong>Sie müssen nichts tun</strong>! Bei Express-Umzügen ist Zeit der kritische Faktor – deshalb übernehmen wir <strong>alles</strong>:<br><br><strong>Wir erledigen:</strong><br>• Verpackung (bringen Material mit)<br>• Abbau aller Möbel<br>• Transport<br>• Zollabwicklung<br>• Aufbau am neuen Standort<br><br><strong>Sie sollten nur:</strong><br>• Wichtige Dokumente bereithalten<br>• Wertgegenstände kennzeichnen<br>• Schlüssel bereithalten<br><br>Lehnen Sie sich zurück – wir machen alles!'
    },
    {
      question: 'Ist mein Umzugsgut bei einem Express-Umzug versichert?',
      answer: 'Ja, <strong>vollständig versichert</strong> – trotz Zeitdruck arbeiten wir sorgfältig!<br><br><strong>Versicherungsschutz:</strong><br>• Transportversicherung inklusive<br>• Deckung bis CHF 100\'000<br>• Auch bei Express-Transporten<br>• Keine Ausschlüsse wegen Zeitdruck<br><br><strong>Unsere Garantie:</strong><br>Schnell heisst nicht unsorgfältig! Wir haben jahrelange Erfahrung mit Express-Umzügen und behandeln Ihre Sachen mit grösster Sorgfalt – egal wie schnell es gehen muss.'
    },
    {
      question: 'Können Sie auch kurzfristig grosse Wohnungen oder Häuser umziehen?',
      answer: 'Ja, auch bei <strong>grossen Umzügen</strong> sind Express-Umzüge möglich!<br><br><strong>Kleine Wohnungen (1-3 Zimmer):</strong><br>• Express innerhalb 24-48h meist möglich<br>• Standard Express-Aufpreise<br><br><strong>Grosse Wohnungen/Häuser (4+ Zimmer):</strong><br>• Express möglich, aber realistisch 2-4 Tage<br>• Wir setzen grösseres Team ein<br>• Höherer Aufpreis wegen Koordination<br><br><strong>Firmenumzüge:</strong><br>• Express möglich übers Wochenende<br>• Montag können Sie weiterarbeiten<br><br>Kontaktieren Sie uns – wir finden eine Lösung!'
    },
    {
      question: 'Was passiert, wenn ich den Express-Umzug verschieben muss?',
      answer: 'Bei Express-Umzügen ist die Situation oft <strong>dynamisch</strong> – wir verstehen das!<br><br><strong>Verschiebung möglich:</strong><br>• Bis 6 Stunden vorher: kostenlos<br>• Kurzfristiger (unter 6h): 20% Stornogebühr<br>• Bei Stornierung: 30% des Aufpreises<br><br><strong>Flexibilität:</strong><br>• Wir versuchen immer einen neuen Termin zu finden<br>• Auch kurzfristige Vorverlegungen möglich<br>• Bei Notfällen kulante Lösung<br><br>Kommunizieren Sie offen mit uns – wir sind sehr flexibel und finden eine Lösung!'
    },
    {
      question: 'Kann ich auch nur Teile meines Hausrats per Express umziehen lassen?',
      answer: 'Ja, <strong><Link to="/teilumzug" className="text-slate-900 font-semibold hover:underline">Teilumzüge</Link> sind auch per Express möglich</strong>!<br><br><strong>Typische Express-Teilumzüge:</strong><br>• Nur die wichtigsten Möbel und Kleider<br>• Rest kommt später mit regulärem Transport<br>• Spart Kosten beim Express-Aufpreis<br><br><strong>Beispiel:</strong><br>• Express: Bett, Kleiderschrank, Kleider, Laptop<br>• Regulär: Restliche Möbel, Kisten, Deko<br><br><strong>Vorteil:</strong><br>• Deutlich günstiger als alles per Express<br>• Sie können sofort einziehen<br>• Rest kommt 1-2 Wochen später<br><br>Sehr beliebte Option bei Express-Umzügen von <Link to="/umzug-basel-deutschland" className="text-slate-900 font-semibold hover:underline">Basel nach Deutschland</Link> oder anderen Routen!'
    },
    {
      question: 'Wie kann ich einen Express-Umzug buchen?',
      answer: 'Die Buchung ist <strong>ganz einfach und schnell</strong>:<br><br><strong>1. Kontakt aufnehmen:</strong><br>• Telefon: <strong>0800 700 100</strong> (bevorzugt!)<br>• Kontaktformular auf dieser Seite<br>• WhatsApp (bei Verfügbarkeit)<br><br><strong>2. Sofort-Beratung (30 Min):</strong><br>• Kurze Bedarfsermittlung<br>• Termin festlegen<br>• Sofort-Offerte erhalten<br><br><strong>3. Zusage & Start:</strong><br>• Bei Zusage: sofortiger Start<br>• Verpackungsmaterial wird bereitgestellt<br>• Umzug beginnt<br><br><strong>Tipp:</strong> Am schnellsten geht es telefonisch – wir können sofort alle Details klären!'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <section className="relative bg-gradient-to-br from-orange-600 via-red-600 to-red-700 text-white py-20 min-h-[700px] overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
            backgroundSize: '40px 40px'
          }} />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-semibold mb-6 border border-white/20">
              <Zap className="w-4 h-4" />
              <span>Express-Service verfügbar</span>
            </div>

            <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
              Express-Umzüge Schweiz → Deutschland
            </h1>

            <p className="text-xl md:text-2xl text-white/90 mb-8 leading-relaxed">
              Wenn es schnell gehen muss – Ihr Umzug in 24-48 Stunden
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <a
                href="tel:0800700100"
                className="bg-white text-red-600 px-8 py-4 rounded-lg font-semibold hover:bg-slate-100 transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 flex items-center justify-center space-x-2"
              >
                <Phone className="w-5 h-5" />
                <span>Jetzt anrufen: 0800 700 100</span>
              </a>
              <button
                onClick={scrollToContact}
                className="bg-white/10 backdrop-blur-sm text-white px-8 py-4 rounded-lg font-semibold hover:bg-white/20 transition-all duration-300 border border-white/20 flex items-center justify-center space-x-2"
              >
                <span>Sofort-Offerte</span>
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-3xl mx-auto">
              {benefits.slice(0, 3).map((benefit, index) => (
                <div key={index} className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                  <Zap className="w-6 h-6 text-yellow-300 mb-2 mx-auto" />
                  <p className="text-sm font-medium">{benefit}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
                Was ist ein Express-Umzug?
              </h2>
              <p className="text-xl text-slate-600 leading-relaxed">
                Ein Express-Umzug ist ein <strong>beschleunigter Umzugsservice</strong> für Situationen, in denen Zeit der entscheidende Faktor ist. Ob unerwartete Jobzusage, Wohnungskündigung oder familiäre Notfälle – wir organisieren Ihren kompletten <Link to="/komplettumzug" className="text-slate-900 font-semibold hover:underline">Umzug von der Schweiz nach Deutschland</Link> in <strong>nur 24-48 Stunden</strong>. Dabei arbeiten wir mit priorisierter <Link to="/zollabwicklung" className="text-slate-900 font-semibold hover:underline">Zollabwicklung</Link> und stellen sicher, dass Sie so schnell wie möglich in Ihrer neuen Wohnung sind.
              </p>
            </div>

            <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-2xl p-8 border-2 border-red-200">
              <h3 className="text-2xl font-bold text-slate-900 mb-6 text-center">
                Alle Vorteile auf einen Blick:
              </h3>
              <div className="grid md:grid-cols-2 gap-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <CheckCircle2 className="w-5 h-5 text-red-600 flex-shrink-0" />
                    <span className="text-slate-700">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
                Wann brauche ich einen Express-Umzug?
              </h2>
              <p className="text-xl text-slate-600">
                Typische Situationen für dringende Umzüge
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {urgentSituations.map((situation, index) => {
                const Icon = situation.icon;
                return (
                  <div key={index} className="bg-white rounded-2xl p-8 border-2 border-slate-100 hover:border-red-300 transition-all duration-300">
                    <div className="bg-red-100 w-14 h-14 rounded-xl flex items-center justify-center mb-6">
                      <Icon className="w-7 h-7 text-red-600" />
                    </div>
                    <h3 className="text-2xl font-bold text-slate-900 mb-3">{situation.title}</h3>
                    <p className="text-slate-600 mb-4 leading-relaxed">{situation.description}</p>
                    <div className="bg-red-50 rounded-lg p-3 border border-red-200">
                      <div className="text-xs text-red-600 font-semibold mb-1">ZEITRAHMEN</div>
                      <div className="text-sm font-bold text-slate-900">{situation.timeframe}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
                Express-Pakete
              </h2>
              <p className="text-xl text-slate-600">
                Verschiedene Geschwindigkeiten für jeden Notfall
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {expressPackages.map((pkg, index) => (
                <div
                  key={index}
                  className={`rounded-2xl p-8 border-2 transition-all duration-300 ${
                    pkg.popular
                      ? 'bg-red-600 border-red-600 text-white scale-105'
                      : 'bg-slate-50 border-slate-200 hover:border-red-300'
                  }`}
                >
                  {pkg.popular && (
                    <div className="bg-yellow-400 text-slate-900 text-xs font-bold px-3 py-1 rounded-full inline-block mb-4">
                      BELIEBTESTE OPTION
                    </div>
                  )}
                  <div className={`flex items-center space-x-2 mb-4 ${pkg.popular ? 'text-white' : 'text-red-600'}`}>
                    <Zap className="w-6 h-6" />
                    <h3 className="text-2xl font-bold">{pkg.title}</h3>
                  </div>
                  <p className={`text-sm mb-2 ${pkg.popular ? 'text-red-100' : 'text-slate-600'}`}>
                    {pkg.timeframe}
                  </p>
                  <p className={`text-xs mb-4 ${pkg.popular ? 'text-red-200' : 'text-slate-500'}`}>
                    {pkg.description}
                  </p>
                  <div className={`text-3xl font-bold mb-6 ${pkg.popular ? 'text-white' : 'text-slate-900'}`}>
                    {pkg.price}
                  </div>
                  <div className={`border-t mb-6 ${pkg.popular ? 'border-red-500' : 'border-slate-300'}`} />
                  <ul className="space-y-3">
                    {pkg.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start space-x-2">
                        <CheckCircle2
                          className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                            pkg.popular ? 'text-yellow-300' : 'text-green-600'
                          }`}
                        />
                        <span className={`text-sm ${pkg.popular ? 'text-red-50' : 'text-slate-700'}`}>
                          {feature}
                        </span>
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={scrollToContact}
                    className={`w-full mt-8 px-6 py-3 rounded-lg font-semibold transition-all duration-300 ${
                      pkg.popular
                        ? 'bg-white text-red-600 hover:bg-slate-100'
                        : 'bg-red-600 text-white hover:bg-red-700'
                    }`}
                  >
                    Jetzt anfragen
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
                So läuft Ihr Express-Umzug ab
              </h2>
              <p className="text-xl text-slate-600">
                Von der Anfrage bis zum Einzug in Rekordzeit
              </p>
            </div>

            <div className="grid md:grid-cols-5 gap-6">
              {howItWorks.map((step, index) => (
                <div key={index} className="bg-white rounded-2xl p-6 border-2 border-slate-100">
                  <div className="bg-red-600 text-white w-12 h-12 rounded-xl flex items-center justify-center font-bold text-xl mb-4">
                    {step.step}
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 mb-3">{step.title}</h3>
                  <p className="text-sm text-slate-600 mb-4 leading-relaxed">{step.description}</p>
                  <div className="bg-red-50 rounded-lg px-3 py-2 border border-red-200">
                    <div className="text-xs text-red-600 font-semibold">⏱ {step.duration}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
                Warum Express-Umzüge bei uns?
              </h2>
              <p className="text-xl text-slate-600">
                Schnell, sicher und professionell
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {whyChooseUs.map((item, index) => {
                const Icon = item.icon;
                return (
                  <div key={index} className="bg-slate-50 rounded-2xl p-8 border-2 border-slate-100 hover:border-red-300 transition-all duration-300 hover:shadow-lg">
                    <div className="bg-red-600 w-14 h-14 rounded-xl flex items-center justify-center mb-6">
                      <Icon className="w-7 h-7 text-white" />
                    </div>
                    <h3 className="text-2xl font-bold text-slate-900 mb-3">{item.title}</h3>
                    <p className="text-slate-600 leading-relaxed">{item.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
                Express-Zusatzleistungen
              </h2>
              <p className="text-xl text-slate-600">
                Alles für Ihren schnellen Umzug
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {expressServices.map((service, index) => {
                const Icon = service.icon;
                return (
                  <div key={index} className="bg-white rounded-2xl p-8 border-2 border-slate-100">
                    <div className="bg-red-100 w-14 h-14 rounded-xl flex items-center justify-center mb-6">
                      <Icon className="w-7 h-7 text-red-600" />
                    </div>
                    <h3 className="text-2xl font-bold text-slate-900 mb-3">{service.title}</h3>
                    <p className="text-slate-600 leading-relaxed">{service.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
                Was Sie vorbereiten sollten
              </h2>
              <p className="text-xl text-slate-600">
                Minimale Vorbereitung für maximale Geschwindigkeit
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {preparation.map((section, index) => (
                <div key={index} className="bg-slate-50 rounded-2xl p-8 border-2 border-slate-100">
                  <h3 className="text-2xl font-bold text-slate-900 mb-6">{section.title}</h3>
                  <ul className="space-y-3">
                    {section.items.map((item, idx) => (
                      <li key={idx} className="flex items-start space-x-2">
                        <CheckCircle2 className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                        <span className="text-slate-600">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
                Express-Aufpreise
              </h2>
              <p className="text-xl text-slate-600">
                Transparente Preise für schnellen Service
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {pricing.map((price, index) => (
                <div key={index} className="bg-white rounded-2xl p-6 border-2 border-slate-100">
                  <h3 className="text-xl font-bold text-slate-900 mb-2">{price.situation}</h3>
                  <div className="text-3xl font-bold text-red-600 mb-2">{price.surcharge}</div>
                  <p className="text-sm text-slate-600 mb-4">{price.description}</p>
                  <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                    <div className="text-xs text-slate-500 mb-1">Beispiel:</div>
                    <div className="text-sm font-semibold text-slate-900">{price.example}</div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 text-center bg-red-50 rounded-xl p-6 border-2 border-red-200">
              <p className="text-slate-700">
                <strong>Wichtig:</strong> Der Aufpreis bezieht sich auf den regulären Umzugspreis. Die Grundkosten hängen von Volumen, Distanz und Zusatzleistungen ab.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
                Das sagen unsere Express-Kunden
              </h2>
              <p className="text-xl text-slate-600">
                Schnell geholfen in dringenden Situationen
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {testimonials.map((testimonial, index) => (
                <div key={index} className="bg-slate-50 rounded-2xl p-6 border-2 border-slate-100">
                  <div className="flex items-center mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <p className="text-slate-700 mb-4 leading-relaxed">"{testimonial.text}"</p>
                  <div className="border-t border-slate-200 pt-4">
                    <div className="font-semibold text-slate-900">{testimonial.name}</div>
                    <div className="text-sm text-slate-500">{testimonial.location}</div>
                    <div className="text-xs text-red-600 font-semibold mt-1">{testimonial.situation}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
                Häufig gestellte Fragen
              </h2>
              <p className="text-xl text-slate-600">
                Alles über Express-Umzüge
              </p>
            </div>

            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div
                  key={index}
                  className="bg-white rounded-xl border-2 border-slate-100 overflow-hidden transition-all duration-300 hover:border-red-300"
                >
                  <button
                    onClick={() => toggleFaq(index)}
                    className="w-full px-6 py-5 flex items-center justify-between text-left"
                  >
                    <span className="font-semibold text-slate-900 pr-4">{faq.question}</span>
                    <ChevronDown
                      className={`w-5 h-5 text-slate-600 transition-transform duration-300 flex-shrink-0 ${
                        openFaq === index ? 'transform rotate-180' : ''
                      }`}
                    />
                  </button>
                  {openFaq === index && (
                    <div className="px-6 pb-5">
                      <p className="text-slate-600 leading-relaxed" dangerouslySetInnerHTML={{ __html: faq.answer }} />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-gradient-to-br from-red-600 to-orange-600">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center text-white">
            <Zap className="w-16 h-16 mx-auto mb-6" />
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Brauchen Sie einen Express-Umzug?
            </h2>
            <p className="text-xl mb-8 text-white/90">
              Rufen Sie uns jetzt an – wir sind sofort für Sie da!
            </p>
            <a
              href="tel:0800700100"
              className="inline-flex items-center space-x-2 bg-white text-red-600 px-8 py-4 rounded-lg font-bold text-lg hover:bg-slate-100 transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105"
            >
              <Phone className="w-6 h-6" />
              <span>0800 700 100</span>
            </a>
            <p className="mt-4 text-sm text-white/80">
              24/7 erreichbar für Express-Anfragen
            </p>
          </div>
        </div>
      </section>

      <Contact />
    </div>
  );
}
